/**
 * Lifecycle checkpoint reached by a `runCode` invocation. Progression:
 * `ENQUEUED` → `API_REG_AWAITING` → `API_REG_DONE` → `AUTH_TOKEN_AWAITING`
 * → `AUTH_TOKEN_DONE` → `RUN_POSTED` → `STDIN_REQUESTED` ↔ `STDIN_RESPONDED`
 * → `FINISHED`. Reported on the hang beacon so the dashboard can answer
 * "which await is stuck?".
 */
export type RunCheckpoint =
  | 'ENQUEUED'
  | 'API_REG_AWAITING'
  | 'API_REG_DONE'
  | 'AUTH_TOKEN_AWAITING'
  | 'AUTH_TOKEN_DONE'
  | 'RUN_POSTED'
  | 'STDIN_REQUESTED'
  | 'STDIN_RESPONDED'
  | 'FINISHED';

/**
 * Delay before the first beacon. Closed-on-the-left interval
 * `[runStart, runStart + INITIAL_HANG_TIMEOUT_MS)`.
 */
export const INITIAL_HANG_TIMEOUT_MS = 30_000;

/**
 * Follow-up cadence after the first beacon. Closed-on-the-left interval
 * `[lastBeaconAt, lastBeaconAt + FOLLOWUP_HANG_TIMEOUT_MS)`.
 */
export const FOLLOWUP_HANG_TIMEOUT_MS = 60_000;

/**
 * Maximum number of beacons per run. Bounds the volume from an
 * abandoned tab that never reaches `FINISHED`.
 */
export const MAX_HANG_FIRINGS = 20;

/**
 * Payload handed to the host on each watchdog firing. The host attaches
 * user/build context and writes the doc.
 */
export interface RunHangReportInput {
  /** Opaque per-run id from {@link RunRecord.runId}. */
  runId: string;
  /** Most recent checkpoint advanced on the record. */
  lastCheckpoint: RunCheckpoint;
  /** Milliseconds since `lastCheckpoint` was entered, at firing time. */
  msStuckAtCheckpoint: number;
  /** Worker has asked main for stdin and main hasn't replied. */
  awaitingStdin: boolean;
  /** Sync-channel message id of the pending stdin reply, or `null`. */
  pendingStdinMessageId: string | null;
  /** Final `lastCheckpoint` of the prior run on this plug, or `null`. */
  previousRunOutcome: RunCheckpoint | null;
  /** `testMode` option for this `runCode` call, if any. */
  testMode: unknown;
  /** `stepMode` option for this `runCode` call. */
  stepMode: boolean;
  /** 0 for the initial fire; increments per follow-up. */
  firingIndex: number;
}

/** Handle returned by {@link scheduleHangWatchdog}. */
export interface RunHangWatchdogHandle {
  /**
   * Advance the lifecycle checkpoint. Idempotent on the same value.
   * Reaching `FINISHED` does not cancel the timer — the caller's
   * `finally` block owns cancellation.
   *
   * @param next Checkpoint just reached.
   * @param now Wall-clock for the advance. Defaults to `new Date()` when omitted.
   */
  checkpoint: (next: RunCheckpoint, now?: Date) => void;
  /** Cancel the timer. Safe to call multiple times. */
  cancel: () => void;
  /** Current checkpoint; used by the caller to record `previousRunOutcome`. */
  getCurrentCheckpoint: () => RunCheckpoint;
}

/**
 * Schedule a beacon timer for a single `runCode` call. Fires `onTimeout`
 * after {@link INITIAL_HANG_TIMEOUT_MS}, then on the
 * {@link FOLLOWUP_HANG_TIMEOUT_MS} cadence, up to {@link MAX_HANG_FIRINGS}
 * total. Reads checkpoint/stdin state via closures at firing time.
 *
 * Tests control timing via `vi.useFakeTimers()`; production uses the
 * platform's `setTimeout`/`clearTimeout` directly.
 *
 * @param input Run identity, initial checkpoint, and option flags.
 * @param onTimeout Per-firing callback. Throws are caught and debug-logged.
 * @param getPreviousRunOutcome Returns the prior run's final checkpoint, or `null`.
 * @param getStdinState Returns live `awaitingStdin` / `pendingStdinMessageId`.
 * @param now Wall-clock source. Defaults to `() => new Date()`.
 * @return Handle for advancing checkpoints and cancelling the timer.
 *
 * @throws Never. Caller-thrown errors from `onTimeout` are forwarded to `console.debug`.
 */
export function scheduleHangWatchdog({
  input,
  onTimeout,
  getPreviousRunOutcome,
  getStdinState,
  now,
}: {
  input: {
    runId: string;
    initialCheckpoint: RunCheckpoint;
    testMode: unknown;
    stepMode: boolean;
  };
  onTimeout: (report: RunHangReportInput) => void;
  getPreviousRunOutcome: () => RunCheckpoint | null;
  getStdinState: () => {
    awaitingStdin: boolean;
    pendingStdinMessageId: string | null;
  };
  now?: () => Date;
}): RunHangWatchdogHandle {
  const clock = now ?? (() => new Date());

  let currentCheckpoint: RunCheckpoint = input.initialCheckpoint;
  let checkpointEnteredAtMs = clock().getTime();
  let firingIndex = 0;
  let timerHandle: ReturnType<typeof setTimeout> | null = null;
  let cancelled = false;

  const fire = () => {
    if (cancelled) {
      return;
    }
    if (currentCheckpoint === 'FINISHED') {
      return;
    }
    const msStuck = clock().getTime() - checkpointEnteredAtMs;
    const stdinState = getStdinState();
    const report: RunHangReportInput = {
      runId: input.runId,
      lastCheckpoint: currentCheckpoint,
      msStuckAtCheckpoint: msStuck,
      awaitingStdin: stdinState.awaitingStdin,
      pendingStdinMessageId: stdinState.pendingStdinMessageId,
      previousRunOutcome: getPreviousRunOutcome(),
      testMode: input.testMode,
      stepMode: input.stepMode,
      firingIndex,
    };
    firingIndex += 1;
    try {
      onTimeout(report);
    } catch (error) {
      console.debug('[runHangWatchdog] onTimeout threw', error);
    }
    if (cancelled || firingIndex >= MAX_HANG_FIRINGS) {
      return;
    }
    timerHandle = setTimeout(fire, FOLLOWUP_HANG_TIMEOUT_MS);
  };

  timerHandle = setTimeout(fire, INITIAL_HANG_TIMEOUT_MS);

  return {
    checkpoint: (next, atTime) => {
      if (next === currentCheckpoint) {
        return;
      }
      currentCheckpoint = next;
      checkpointEnteredAtMs = (atTime ?? clock()).getTime();
    },
    cancel: () => {
      if (cancelled) {
        return;
      }
      cancelled = true;
      if (timerHandle !== null) {
        clearTimeout(timerHandle);
        timerHandle = null;
      }
    },
    getCurrentCheckpoint: () => currentCheckpoint,
  };
}
